import 'src/lib/polyfills';

import { useMemo } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { LastLocationProvider } from 'react-router-last-location';
import { ApolloProvider } from '@apollo/client';
import { compose } from 'recompose';

import { ErrorBoundary } from 'src/components/ErrorBoundary';
import { isApplicationModeInfinity } from 'src/data/system';
import { gqlClient } from 'src/graphql/client';
import { wrapWith } from 'src/lib/react';
import {
  CustomerFeatureLimitsGuard,
  PopupService,
  useImpactClickId,
  useMaintainBrandVoicesStoreState,
  useMaintainPerformanceStoreState,
  useMaintainProjectFolderStoreState,
  useMaintainTalkingPointsStoreState,
  UserGuard,
  useSetupSystemStoreData,
  useTrackApplicationLoaded,
  useWebinarBanner,
} from 'src/services';
import { useMaintainResourceStoreState } from 'src/services/useMaintainResourceStoreState';
import { useMaintainVisualLanguageStoreState } from 'src/services/useMaintainVisualLanguageStoreState';
import { useIsCurrentUserReviewer } from 'src/store/user/useIsCurrentUserReviewer';
import { SubscriptionPausedWall } from 'src/views/SubscriptionPausedWall';

import AuthorCompleteTracer from './services/AuthorCompleteTracer';
import AuthTokenGuard from './services/AuthTokenGuard';
import { BackendDataUpdater, ReviewerDataUpdater } from './services/BackendDataUpdater';
import BuildRelevanceChecker from './services/BuildRelevanceChecker';
import CustomerSelectionSaver from './services/CustomerSelectionSaver';
import { SupportExternalInfinity, SupportParentInfinity } from './services/infinity';
import { LocationGuard } from './services/LocationGuard';
import NotificationsService from './services/notifications';
import { ThemeAndStylesProvider } from './services/ThemeAndStylesProvider';
import { useMaintainTemplatesStoreState } from './services/useMaintainTemplatesStoreState';
import { UserAnalyticsUpdater } from './services/UserAnalyticsUpdater';
import { ZohoVisitorUpdater } from './services/ZohoVisitorUpdater';
import GlobalStoreContainers from './store/GlobalStoreContainers';
import { ConfirmationDialog } from './views/ConfirmationDialog';
import { DataExtractionIndicator } from './views/DataExtractionIndicator';
import { PopupNotifications } from './views/NotificationsCenter';
import { OnboardingRoot } from './views/OnboardingRoot';
import { AppRouting } from './AppRouting';
import { ReviewerRouting } from './ReviewerRouting';
import { createAppTheme } from './theme';

const AppThemeProvider = (props) => {
  const theme = useMemo(() => createAppTheme(), []);

  return (
    <ThemeAndStylesProvider
      theme={theme}
      {...props}
    />
  );
};

const FullApp = () => {
  useMaintainBrandVoicesStoreState();
  useMaintainVisualLanguageStoreState();
  useMaintainPerformanceStoreState();
  useMaintainResourceStoreState();
  useMaintainTalkingPointsStoreState();
  useMaintainProjectFolderStoreState();
  useWebinarBanner();
  useMaintainTemplatesStoreState();

  return (
    <>
      <BackendDataUpdater />
      <SubscriptionPausedWall />

      {isApplicationModeInfinity() ? (
        <SupportParentInfinity />
      ) : (
        <>
          <SupportExternalInfinity />
          <LocationGuard />
          <OnboardingRoot />
          <DataExtractionIndicator />
          <CustomerFeatureLimitsGuard />
        </>
      )}

      <AppRouting />
    </>
  );
};

// A reviewer sees only the projects shared with them, so none of the account's data is loaded.
const ReviewerApp = () => (
  <>
    <ReviewerDataUpdater />
    <ReviewerRouting />
  </>
);

const App = () => {
  const isReviewer = useIsCurrentUserReviewer();

  useImpactClickId();
  useTrackApplicationLoaded();
  useSetupSystemStoreData();

  return (
    <>
      <Helmet
        titleTemplate="%s - Anyword AI Copywriter"
        defaultTitle="Anyword AI Copywriter"
      />

      <Router>
        <CompatRouter>
          <PopupService>
            <NotificationsService />
            <CustomerSelectionSaver />
            <UserAnalyticsUpdater />
            <ZohoVisitorUpdater />
            <AuthorCompleteTracer />
            <AuthTokenGuard />
            <UserGuard />
            {!isApplicationModeInfinity() && <BuildRelevanceChecker />}

            <LastLocationProvider>
              <PopupNotifications />
              <ConfirmationDialog />

              {isReviewer ? <ReviewerApp /> : <FullApp />}
            </LastLocationProvider>
          </PopupService>
        </CompatRouter>
      </Router>
    </>
  );
};

export default compose(
  wrapWith(ErrorBoundary),
  wrapWith(HelmetProvider),
  wrapWith(ApolloProvider, { client: gqlClient }),
  wrapWith(GlobalStoreContainers),
  wrapWith(AppThemeProvider)
)(App);
